<div class="page-header">
  <div class="process-heading">
    <a class="back-link" [routerLink]="['/processes']" [title]="t('ProcessPageLink')" [attr.aria-label]="t('ProcessPageLink')">◀</a>
    <div>
      <h1>{{ data?.process?.name || t('HubProcessEditorTitle') }}</h1>
      <p>{{ t('HubProcessEditorIntro') }}</p>
    </div>
  </div>
</div>

@if (error) {
  <p class="message error">{{ error }}</p>
}
@if (success) {
  <p class="message success">{{ success }}</p>
}
@if (loading) {
  <p>{{ t('HubProcessEditorLoading') }}</p>
}

@if (!loading && data) {
  <div class="tab-list" role="tablist">
    @for (tab of tabItems; track tab) {
      <button type="button" class="tab-button" role="tab" [attr.aria-selected]="activeTab === tab.value" [class.active]="activeTab === tab.value" (click)="activeTab = tab.value">{{ t(tab.labelKey) }}</button>
    }
  </div>
  <select class="mobile-tab-select" [(ngModel)]="activeTab" aria-label="Process editor section">
    @for (tab of tabItems; track tab) {
      <option [value]="tab.value">{{ t(tab.labelKey) }}</option>
    }
  </select>
  @if (activeTab === 'properties') {
    <section class="card editor-card form-grid">
      <div class="form-grid two-columns">
        <label>
          <span class="field-label">{{ t('processType') }} <hub-help-tooltip [text]="helpText('HubProcessTypeInfoTitle', 'HubProcessTypeInfoText')"></hub-help-tooltip></span>
          <select name="type" [(ngModel)]="data.process.type" (ngModelChange)="markPropertiesDirty()">
            @for (option of data.typeOptions; track option) {
              <option [value]="option.value">{{ option.label }}</option>
            }
          </select>
        </label>
        <label>
          <span class="field-label">{{ t('processLocus') }} <hub-help-tooltip [text]="helpText('HubProcessLocusInfoTitle', 'HubProcessLocusInfoText')"></hub-help-tooltip></span>
          <select name="locus" [(ngModel)]="data.process.locus" (ngModelChange)="markPropertiesDirty()">
            @for (option of data.locusOptions; track option) {
              <option [value]="option.value">{{ option.label }}</option>
            }
          </select>
        </label>
      </div>
      <label>{{ t('HubDescription') }}<textarea name="description" [(ngModel)]="data.process.description" (ngModelChange)="markPropertiesDirty()"></textarea></label>
      <span class="autosave-status">{{ saveUndoRedoLabel() }}</span>
    </section>
  }
  @if (activeTab === 'datamodel') {
    <section class="card editor-card form-grid">
      @if (data.datamodel.error) {
        <p class="message error">{{ data.datamodel.error }}</p>
      }
      @if (datamodelParseError; as errorReport) {
        <div class="message error json-error-report">
          <strong>{{ errorReport.title }}</strong>
          <ul>
            @for (detail of jsonErrorDetails(errorReport); track detail) {
              <li>{{ detail }}</li>
            }
          </ul>
        </div>
      }
      <div class="model-header">
        <div class="editor-actions-stack">
          @if (screenModifiedDate(data.datamodel.lastModified); as modified) {
            <span>{{ t('HubLastChanged') }} {{ modified | date:'d MMM y, HH:mm' }}</span>
          }
          <div class="editor-toolbar">
            <button class="secondary-button" type="button" (click)="datamodelHelpOpen = true"><wa-icon name="question-circle"></wa-icon>{{ t('HubHelp') }}</button>
            <span class="autosave-status">{{ saveUndoRedoLabel() }}</span>
            <div class="undo-redo-controls" role="group" [attr.aria-label]="t('HubUndoRedo')">
              <button class="secondary-button icon-button" type="button" (click)="undoDatamodel()" [disabled]="!canUndoDatamodel()" [title]="t('HubUndo')" [attr.aria-label]="t('HubUndo')"><wa-icon name="arrow-counterclockwise"></wa-icon></button>
              <button class="secondary-button icon-button" type="button" (click)="redoDatamodel()" [disabled]="!canRedoDatamodel()" [title]="t('HubRedo')" [attr.aria-label]="t('HubRedo')"><wa-icon name="arrow-clockwise"></wa-icon></button>
            </div>
            <div class="view-toggle" role="group" [attr.aria-label]="t('HubEditorViewToggle')">
              <button class="view-toggle-button" type="button" [class.active]="datamodelMode === 'gui'" (click)="datamodelMode !== 'gui' && toggleDatamodelMode()" [title]="t('HubShowGui')" [attr.aria-label]="t('HubShowGui')"><wa-icon name="ui-checks-grid"></wa-icon></button>
              <button class="view-toggle-button" type="button" [class.active]="datamodelMode === 'json'" (click)="datamodelMode !== 'json' && toggleDatamodelMode()" [title]="t('HubShowJson')" [attr.aria-label]="t('HubShowJson')"><wa-icon name="braces"></wa-icon></button>
            </div>
          </div>
        </div>
      </div>
      @if (datamodelMode === 'gui' && datamodelVisualReady) {
        <ng-container *ngTemplateOutlet="variablesEditor; context: {variables: datamodelVariables, depth: 0, path: 'root'}"></ng-container>
      } @else {
        @if (datamodelMode === 'gui') {
          <p class="message error">{{ t('HubDataModelVisualUnavailable') }}</p>
        }
      }
      @if (datamodelMode === 'json' || !datamodelVisualReady) {
        <div class="raw-json-panel">
          <div class="raw-json-header">
            <div>
              <h3>{{ t('HubRawJson') }}</h3>
              <p>{{ t('HubRawJsonIntro') }}</p>
            </div>
            <button class="secondary-button" type="button" (click)="applyDatamodelJson()">{{ t('HubApplyJson') }}</button>
          </div>
          <textarea class="json-editor" name="datamodel" [(ngModel)]="data.datamodel.json" (ngModelChange)="onDatamodelJsonChange()"></textarea>
        </div>
      }
    </section>
  }
  <ng-template #variablesEditor let-variables="variables" let-depth="depth" let-path="path">
    <div class="variable-list" [class.child-variable-list]="depth > 0">
      @if (depth > 0) {
        <div class="variable-list-header">
          <div>
            <h3>{{ t('HubChildVariables') }}</h3>
            <p>{{ t('HubChildVariablesIntro') }}</p>
          </div>
        </div>
      }
      @for (variable of variables; track trackVariable(i, variable); let i = $index) {
        <article class="variable-card" [class.child-variable-card]="depth > 0" [class.nesting-depth-0]="depth === 0" [class.nesting-depth-1]="depth === 1" [class.nesting-depth-2]="depth === 2" [class.nesting-depth-3]="depth === 3" [class.nesting-depth-4]="depth === 4" [class.nesting-depth-5]="depth >= 5">
          <div class="variable-card-header">
            <div class="variable-title">
              @if (componentListType(variable.type)) {
                <button class="disclosure-button" type="button" (click)="toggleVariableChildren(variable)" [title]="variable.childrenOpen ? t('HubHideChildVariables') : t('HubShowChildVariables')" [attr.aria-label]="variable.childrenOpen ? t('HubHideChildVariables') : t('HubShowChildVariables')" [attr.aria-expanded]="variable.childrenOpen">
                  <span [class.open]="variable.childrenOpen">▶</span>
                </button>
              }
              @if (!componentListType(variable.type)) {
                <span class="disclosure-placeholder"></span>
              }
              <wa-icon class="type-icon" [name]="typeIcon(variable.type.typeName)" [title]="typeLabel(variable.type.typeName)"></wa-icon>
              <div>
                <strong>{{ variable.name || t('HubUnnamedVariable') }}</strong>
                <span>{{ typeLabel(variable.type.typeName) }}</span>
              </div>
            </div>
            <div class="button-row variable-buttons">
              <button class="secondary-button icon-button" type="button" [class.active]="variable.editorOpen" (click)="toggleVariableEditor(variable)" [title]="variable.editorOpen ? t('HubHideVariableEditor') : t('HubShowVariableEditor')" [attr.aria-label]="variable.editorOpen ? t('HubHideVariableEditor') : t('HubShowVariableEditor')">
                <wa-icon name="sliders"></wa-icon>
              </button>
              <button class="secondary-button icon-button" type="button" [disabled]="i === 0" (click)="moveVariable(variables, i, -1)" [title]="t('HubMoveUp')" [attr.aria-label]="t('HubMoveUp')"><wa-icon name="arrow-up"></wa-icon></button>
              <button class="secondary-button icon-button" type="button" [disabled]="i === variables.length - 1" (click)="moveVariable(variables, i, 1)" [title]="t('HubMoveDown')" [attr.aria-label]="t('HubMoveDown')"><wa-icon name="arrow-down"></wa-icon></button>
              <button class="secondary-button icon-button" type="button" (click)="duplicateVariable(variables, i)" [title]="t('HubDuplicate')" [attr.aria-label]="t('HubDuplicate')"><wa-icon name="copy"></wa-icon></button>
              <button class="danger-button icon-button" type="button" (click)="removeVariable(variables, i)" [title]="t('Delete')" [attr.aria-label]="t('Delete')"><wa-icon name="trash"></wa-icon></button>
            </div>
          </div>
          @if (variable.editorOpen) {
            <div class="form-grid two-columns model-grid">
              <label>
                <span class="field-label">{{ t('HubVariableName') }} <hub-help-tooltip [text]="helpText('HubVariableNameTooltip', 'HubVariableNameHelp')"></hub-help-tooltip></span>
                <input [name]="controlName(path, i, 'name')" [(ngModel)]="variable.name" (ngModelChange)="syncDatamodelField()" [placeholder]="t('HubVariableNamePlaceholder')">
              </label>
              <label>
                <span class="field-label">{{ t('dataType') }} <hub-help-tooltip [text]="helpText('HubDataTypeTooltip', 'HubDataTypeHelp')"></hub-help-tooltip></span>
                <select [name]="controlName(path, i, 'type')" [ngModel]="variable.type.typeName" (ngModelChange)="changeVariableType(variable, $event)">
                  @for (type of dataModelTypes; track type) {
                    <option [value]="type.value">{{ t(type.labelKey) }}</option>
                  }
                </select>
              </label>
              <label>
                <span class="field-label">{{ t('scopeMode') }} <hub-help-tooltip [text]="helpText('HubScopeModeTooltip', 'HubScopeModeHelp')"></hub-help-tooltip></span>
                <select [name]="controlName(path, i, 'scopeMode')" [(ngModel)]="variable.scopeMode" (ngModelChange)="syncDatamodelField()">
                  @for (option of scopeModeOptions; track option) {
                    <option [value]="option.value">{{ t(option.labelKey) }}</option>
                  }
                </select>
              </label>
              <label>
                <span class="field-label">{{ t('mandatory') }} <hub-help-tooltip [text]="helpText('HubMandatoryTooltip', 'HubMandatoryHelp')"></hub-help-tooltip></span>
                <select [name]="controlName(path, i, 'mandatory')" [(ngModel)]="variable.mandatory" (ngModelChange)="syncDatamodelField()">
                  @for (option of mandatoryOptions; track option) {
                    <option [value]="option.value">{{ t(option.labelKey) }}</option>
                  }
                </select>
              </label>
              <label>
                <span class="field-label">{{ t('visibilityCondition') }} <hub-help-tooltip [text]="helpText('HubVisibilityConditionTooltip', 'HubVisibilityConditionHelp')"></hub-help-tooltip></span>
                <input [name]="controlName(path, i, 'visibilityCondition')" [(ngModel)]="variable.visibilityCondition" (ngModelChange)="syncDatamodelField()" placeholder="true">
              </label>
              <label>
                <span class="field-label">{{ t('HubEnabledCondition') }} <hub-help-tooltip [text]="helpText('HubEnabledConditionTooltip', 'HubEnabledConditionHelp')"></hub-help-tooltip></span>
                <input [name]="controlName(path, i, 'enabledCondition')" [(ngModel)]="variable.enabledCondition" (ngModelChange)="syncDatamodelField()" placeholder="true">
              </label>
            </div>
          }
          @if (variable.editorOpen) {
            <section class="type-settings">
              <h4>{{ t('HubTypeSettings') }}</h4>
              <p>{{ t('HubTypeSettingsIntro') }}</p>
              @if (textType(variable.type); as text) {
                <div class="form-grid two-columns model-grid text-settings-grid">
                  <label class="text-maxlength-field">
                    <span class="field-label">{{ t('maxlength') }} <hub-help-tooltip [text]="helpText('HubMaxLengthTooltip', 'HubMaxLengthHelp')"></hub-help-tooltip></span>
                    <input [name]="controlName(path, i, 'textMaxLength')" type="number" min="0" [(ngModel)]="text.maxLength" (ngModelChange)="syncDatamodelField()" [placeholder]="t('HubNoLimit')">
                  </label>
                  <label class="checkbox-label model-checkbox" [title]="t('HubMultilineHelp')">
                    <input [name]="controlName(path, i, 'textMultiline')" type="checkbox" [(ngModel)]="text.multiline" (ngModelChange)="syncDatamodelField()"> {{ t('multiline') }} <hub-help-tooltip [text]="t('HubMultilineHelp')"></hub-help-tooltip>
                  </label>
                  <label class="checkbox-label model-checkbox text-options-toggle" [title]="t('HubTextOptionsHelp')">
                    <input [name]="controlName(path, i, 'textHasOptions')" type="checkbox" [(ngModel)]="text.hasOptions" (ngModelChange)="syncDatamodelField()"> {{ t('multichoice') }} <hub-help-tooltip [text]="t('HubTextOptionsHelp')"></hub-help-tooltip>
                  </label>
                  @if (text.hasOptions) {
                    <div class="options-editor">
                      <div class="variable-list-header compact-header">
                        <div>
                          <h4>{{ t('multichoiceValues') }}</h4>
                          <p>{{ t('HubTextOptionsIntro') }}</p>
                        </div>
                        <button class="secondary-button compact-button" type="button" (click)="addTextOption(text)">{{ t('HubAddOption') }}</button>
                      </div>
                      @for (option of text.optionsList; track option; let optionIndex = $index) {
                        <div class="option-row">
                          <label>
                            {{ t('HubOptionValue') }}
                            <input [name]="controlName(path, i, 'optionValue' + optionIndex)" [(ngModel)]="option.value" (ngModelChange)="syncDatamodelField()" [placeholder]="t('HubOptionValuePlaceholder')">
                          </label>
                          <label>
                            {{ t('HubOptionEnabledCondition') }}
                            <input [name]="controlName(path, i, 'optionCondition' + optionIndex)" [(ngModel)]="option.enabledCondition" (ngModelChange)="syncDatamodelField()" placeholder="true">
                          </label>
                          <button class="danger-button compact-button" type="button" (click)="removeTextOption(text, optionIndex)">{{ t('Delete') }}</button>
                        </div>
                      }
                    </div>
                  }
                </div>
              }
              @if (numberType(variable.type); as number) {
                <div class="form-grid two-columns model-grid">
                  <label>{{ t('HubDefaultValue') }}<input [name]="controlName(path, i, 'numberDefault')" type="number" [(ngModel)]="number.defaultValue" (ngModelChange)="syncDatamodelField()"></label>
                  <label>{{ t('min') }}<input [name]="controlName(path, i, 'numberMin')" type="number" [(ngModel)]="number.minValue" (ngModelChange)="syncDatamodelField()"></label>
                  <label>{{ t('max') }}<input [name]="controlName(path, i, 'numberMax')" type="number" [(ngModel)]="number.maxValue" (ngModelChange)="syncDatamodelField()"></label>
                  <label>{{ t('step') }}<input [name]="controlName(path, i, 'numberStep')" type="number" step="any" [(ngModel)]="number.step" (ngModelChange)="syncDatamodelField()"></label>
                </div>
              }
              @if (lengthType(variable.type); as length) {
                <div class="form-grid two-columns model-grid">
                  <label>
                    <span class="field-label">{{ t('maxlength') }} <hub-help-tooltip [text]="helpText('HubMaxLengthTooltip', 'HubMaxLengthHelp')"></hub-help-tooltip></span>
                    <input [name]="controlName(path, i, 'lengthMax')" type="number" min="0" [(ngModel)]="length.maxLength" (ngModelChange)="syncDatamodelField()" [placeholder]="t('HubNoLimit')">
                  </label>
                </div>
              }
              @if (fileType(variable.type); as file) {
                <div class="form-grid two-columns model-grid">
                  <label>
                    <span class="field-label">{{ t('HubMaxFileSize') }} <hub-help-tooltip [text]="helpText('HubMaxFileSizeTooltip', 'HubMaxFileSizeHelp')"></hub-help-tooltip></span>
                    <input [name]="controlName(path, i, 'fileMaxSize')" type="number" min="0" [(ngModel)]="file.maxSize" (ngModelChange)="syncDatamodelField()" [placeholder]="t('HubNoLimit')">
                  </label>
                  <label class="checkbox-label model-checkbox" [title]="t('HubAllowMultipleFilesHelp')">
                    <input [name]="controlName(path, i, 'fileAllowMultiple')" type="checkbox" [(ngModel)]="file.allowMultiple" (ngModelChange)="syncDatamodelField()"> {{ t('HubAllowMultipleFiles') }} <hub-help-tooltip [text]="t('HubAllowMultipleFilesHelp')"></hub-help-tooltip>
                  </label>
                </div>
              }
              @if (componentListType(variable.type); as componentList) {
                <div class="component-list-editor">
                  <div class="form-grid two-columns model-grid">
                    <label>
                      <span class="field-label">{{ t('HubComponentListKey') }} <hub-help-tooltip [text]="helpText('HubComponentListKeyTooltip', 'HubComponentListKeyHelp')"></hub-help-tooltip></span>
                      <input [name]="controlName(path, i, 'componentKey')" [(ngModel)]="componentList.key_variable_name" (ngModelChange)="componentList.id = componentList.key_variable_name; syncDatamodelField()" placeholder="id">
                    </label>
                  </div>
                </div>
              }
              @if (variable.type.typeName === 'Boolean' || variable.type.typeName === 'Country' || variable.type.typeName === 'ChUid') {
                <p class="empty-state">{{ t('HubTypeHasNoSettings') }}</p>
              }
            </section>
          }
          @if (componentListType(variable.type); as componentList) {
            @if (variable.childrenOpen) {
              <ng-container *ngTemplateOutlet="variablesEditor; context: {variables: componentList.children, depth: depth + 1, path: childPath(path, i)}"></ng-container>
            }
          }
        </article>
      }
      @if (variables.length === 0) {
        <p class="empty-state">{{ t('HubNoVariables') }}</p>
      }
      <button class="secondary-button add-variable-button" type="button" (click)="addVariable(variables)"><wa-icon name="plus-lg"></wa-icon>{{ t('HubAddVariable') }}</button>
    </div>
  </ng-template>
  @if (activeTab === 'screens') {
    <section class="card editor-card">
      <div class="screen-layout">
        <div class="screen-list" role="list">
          @for (screen of data.screens; track screen) {
            <button type="button" class="screen-list-item" [class.active]="selectedScreenId === screen.id" (click)="selectedScreenId = screen.id">
              <strong>{{ screen.id }}</strong>
              <span>{{ screen.componentCount }} {{ t('HubComponents') }} · {{ screen.controlCount }} {{ t('HubControls') }}</span>
              @if (screenModifiedDate(screen.lastModified); as modified) {
                <small>{{ t('HubLastChanged') }} {{ modified | date:'d MMM y, HH:mm' }}</small>
              }
            </button>
          }
          @if (data.screens.length === 0) {
            <p class="empty-state">{{ t('HubNoScreens') }}</p>
          }
          <form class="add-screen-form" (ngSubmit)="addScreen()">
            <input name="newScreen" [(ngModel)]="newScreenId" [placeholder]="t('HubScreenName')">
            <button class="primary-button" type="submit"><wa-icon name="plus-lg"></wa-icon>{{ t('HubAddScreen') }}</button>
          </form>
        </div>
        @if (selectedScreen(); as screen) {
          <div class="screen-editor form-grid">
            <div class="screen-editor-header">
              <div>
                <h2>{{ screen.id }}</h2>
                <p>{{ screen.componentCount }} {{ t('HubComponents') }} · {{ screen.controlCount }} {{ t('HubControls') }}</p>
              </div>
              <div class="editor-actions-stack">
                @if (screenModifiedDate(screen.lastModified); as modified) {
                  <span>{{ t('HubLastChanged') }} {{ modified | date:'d MMM y, HH:mm' }}</span>
                }
                <div class="editor-toolbar">
                  <span class="autosave-status">{{ saveUndoRedoLabel() }}</span>
                  <div class="undo-redo-controls" role="group" [attr.aria-label]="t('HubUndoRedo')">
                    <button class="secondary-button icon-button" type="button" (click)="undoScreen(screen)" [disabled]="!canUndoScreen(screen)" [title]="t('HubUndo')" [attr.aria-label]="t('HubUndo')"><wa-icon name="arrow-counterclockwise"></wa-icon></button>
                    <button class="secondary-button icon-button" type="button" (click)="redoScreen(screen)" [disabled]="!canRedoScreen(screen)" [title]="t('HubRedo')" [attr.aria-label]="t('HubRedo')"><wa-icon name="arrow-clockwise"></wa-icon></button>
                  </div>
                  <div class="view-toggle" role="group" [attr.aria-label]="t('HubEditorViewToggle')">
                    <button class="view-toggle-button" type="button" [class.active]="screenState(screen).mode === 'gui'" (click)="screenState(screen).mode !== 'gui' && toggleScreenMode(screen)" [title]="t('HubShowGui')" [attr.aria-label]="t('HubShowGui')"><wa-icon name="ui-checks-grid"></wa-icon></button>
                    <button class="view-toggle-button" type="button" [class.active]="screenState(screen).mode === 'json'" (click)="screenState(screen).mode !== 'json' && toggleScreenMode(screen)" [title]="t('HubShowJson')" [attr.aria-label]="t('HubShowJson')"><wa-icon name="braces"></wa-icon></button>
                  </div>
                </div>
              </div>
            </div>
            @if (screen.error) {
              <p class="message error">{{ screen.error }}</p>
            }
            @if (screenState(screen).parseError; as errorReport) {
              <div class="message error json-error-report">
                <strong>{{ errorReport.title }}</strong>
                <ul>
                  @for (detail of jsonErrorDetails(errorReport); track detail) {
                    <li>{{ detail }}</li>
                  }
                </ul>
              </div>
            }
            @if (screenState(screen).mode === 'gui' && screenState(screen).visualReady) {
              <ng-container *ngTemplateOutlet="screenElementsEditor; context: {elements: screenState(screen).elements, depth: 0, path: screen.id}"></ng-container>
            } @else {
              <div class="raw-json-panel">
                <div class="raw-json-header">
                  <div>
                    <h3>{{ t('HubRawJson') }}</h3>
                    <p>{{ t('HubScreenRawJsonIntro') }}</p>
                  </div>
                  <button class="secondary-button" type="button" (click)="applyScreenJson(screen)">{{ t('HubApplyJson') }}</button>
                </div>
                <textarea class="json-editor" [(ngModel)]="screen.json" [name]="'screen-' + screen.id" (ngModelChange)="onScreenJsonChange(screen)"></textarea>
              </div>
            }
          </div>
        }
      </div>
    </section>
  }
  @if (datamodelHelpOpen) {
    <div class="modal-backdrop" (click)="datamodelHelpOpen = false">
      <section class="modal-card datamodel-help-modal" (click)="$event.stopPropagation()">
        <h2>{{ t('HubDataModelHelpTitle') }}</h2>
        <article class="info-box">
          <strong>{{ t('HubDataModelNameHelpTitle') }}</strong>
          <p>{{ t('HubDataModelNameHelpText') }}</p>
        </article>
        <article class="info-box">
          <strong>{{ t('HubDataModelConditionsHelpTitle') }}</strong>
          <p>{{ t('HubDataModelConditionsHelpText') }}</p>
        </article>
        <article class="info-box">
          <strong>{{ t('HubDataModelScopeHelpTitle') }}</strong>
          <p>{{ t('HubDataModelScopeHelpText') }}</p>
        </article>
        <div class="button-row">
          <button class="secondary-button" type="button" (click)="datamodelHelpOpen = false">{{ t('DeleteFormCancelButtonLabel') }}</button>
        </div>
      </section>
    </div>
  }
  <ng-template #screenElementsEditor let-elements="elements" let-depth="depth" let-path="path">
    <div class="variable-list" [class.child-variable-list]="depth > 0">
      @if (depth > 0) {
        <div class="variable-list-header">
          <h3>{{ depth === 0 ? t('HubScreenElements') : t('HubScreenChildElements') }}</h3>
        </div>
      }
      @for (element of elements; track trackScreenElement(i, element); let i = $index) {
        <article class="variable-card screen-element-card" [class.child-variable-card]="depth > 0" [class.nesting-depth-0]="depth === 0" [class.nesting-depth-1]="depth === 1" [class.nesting-depth-2]="depth === 2" [class.nesting-depth-3]="depth === 3" [class.nesting-depth-4]="depth === 4" [class.nesting-depth-5]="depth >= 5">
          <div class="variable-card-header">
            <div class="variable-title">
              @if (hasScreenChildren(element)) {
                <button class="disclosure-button" type="button" (click)="toggleScreenElementChildren(element)" [title]="element.childrenOpen ? t('HubHideChildVariables') : t('HubShowChildVariables')" [attr.aria-label]="element.childrenOpen ? t('HubHideChildVariables') : t('HubShowChildVariables')" [attr.aria-expanded]="element.childrenOpen">
                  <span [class.open]="element.childrenOpen">▶</span>
                </button>
              }
              @if (!hasScreenChildren(element)) {
                <span class="disclosure-placeholder"></span>
              }
              <wa-icon class="type-icon" [name]="screenElementIcon(element)" [title]="screenElementLabel(element)"></wa-icon>
              <div>
                <strong>{{ screenElementTitle(element) }}</strong>
                <span>{{ screenElementLabel(element) }}</span>
              </div>
            </div>
            <div class="button-row variable-buttons">
              <button class="secondary-button icon-button" type="button" [class.active]="element.editorOpen" (click)="toggleScreenElementEditor(element)" [title]="element.editorOpen ? t('HubHideVariableEditor') : t('HubShowVariableEditor')" [attr.aria-label]="element.editorOpen ? t('HubHideVariableEditor') : t('HubShowVariableEditor')">
                <wa-icon name="sliders"></wa-icon>
              </button>
              <button class="secondary-button icon-button" type="button" [disabled]="i === 0" (click)="moveScreenElement(elements, i, -1)" [title]="t('HubMoveUp')" [attr.aria-label]="t('HubMoveUp')"><wa-icon name="arrow-up"></wa-icon></button>
              <button class="secondary-button icon-button" type="button" [disabled]="i === elements.length - 1" (click)="moveScreenElement(elements, i, 1)" [title]="t('HubMoveDown')" [attr.aria-label]="t('HubMoveDown')"><wa-icon name="arrow-down"></wa-icon></button>
              <button class="secondary-button icon-button" type="button" (click)="duplicateScreenElement(elements, i)" [title]="t('HubDuplicate')" [attr.aria-label]="t('HubDuplicate')"><wa-icon name="copy"></wa-icon></button>
              <button class="danger-button icon-button" type="button" (click)="removeScreenElement(elements, i)" [title]="t('Delete')" [attr.aria-label]="t('Delete')"><wa-icon name="trash"></wa-icon></button>
            </div>
          </div>
          @if (element.editorOpen) {
            <div class="form-grid two-columns model-grid">
              <label>
                <span class="field-label">{{ t('HubScreenElementType') }} <hub-help-tooltip [text]="t('HubScreenElementTypeHelp')"></hub-help-tooltip></span>
                <select [name]="screenControlName(path, i, 'type')" [ngModel]="element.type" (ngModelChange)="changeScreenElementType(element, $event)">
                  @for (type of screenElementTypes; track type) {
                    <option [value]="type.value">{{ t(type.labelKey) }}</option>
                  }
                </select>
              </label>
              <label>
                <span class="field-label">{{ t('visibilityCondition') }} <hub-help-tooltip [text]="t('HubVisibilityConditionHelp')"></hub-help-tooltip></span>
                <input [name]="screenControlName(path, i, 'visibilityCondition')" [(ngModel)]="element.visibilityCondition" (ngModelChange)="syncScreenField()" placeholder="true">
              </label>
              @if (element.type === 'control' || element.type === 'componentlist') {
                <label>
                  <span class="field-label">{{ t('HubScreenBinding') }} <hub-help-tooltip [text]="t('HubScreenBindingHelp')"></hub-help-tooltip></span>
                  <input [name]="screenControlName(path, i, 'binding')" [(ngModel)]="element.binding" (ngModelChange)="syncScreenField()" placeholder="variableName">
                </label>
              }
              @if (element.type === 'control') {
                <label>
                  <span class="field-label">{{ t('LabelLabel') }} <hub-help-tooltip [text]="t('HubScreenControlLabelHelp')"></hub-help-tooltip></span>
                  <input [name]="screenControlName(path, i, 'label')" [(ngModel)]="element.label" (ngModelChange)="syncScreenField()" placeholder="translation_label">
                </label>
              }
              @if (element.type === 'title') {
                <label>
                  <span class="field-label">{{ t('HubScreenTextLabel') }} <hub-help-tooltip [text]="t('HubScreenTextHelp')"></hub-help-tooltip></span>
                  <input [name]="screenControlName(path, i, 'text')" [(ngModel)]="element.text" (ngModelChange)="syncScreenField()" placeholder="translation_label">
                </label>
              }
              @if (element.type === 'label' || element.type === 'infobox') {
                <label>
                  <span class="field-label">{{ t('HubScreenTextSource') }} <hub-help-tooltip [text]="t('HubScreenTextSourceHelp')"></hub-help-tooltip></span>
                  <select [name]="screenControlName(path, i, 'textSource')" [(ngModel)]="element.textSource" (ngModelChange)="syncScreenField()">
                    @for (source of screenTextSourceOptions; track source) {
                      <option [value]="source.value">{{ t(source.labelKey) }}</option>
                    }
                  </select>
                </label>
              }
              @if ((element.type === 'label' || element.type === 'infobox') && element.textSource === 'text') {
                <label>
                  <span class="field-label">{{ t('HubScreenTextLabel') }} <hub-help-tooltip [text]="t('HubScreenTextHelp')"></hub-help-tooltip></span>
                  <input [name]="screenControlName(path, i, 'text')" [(ngModel)]="element.text" (ngModelChange)="syncScreenField()" placeholder="translation_label">
                </label>
              }
              @if ((element.type === 'label' || element.type === 'infobox') && element.textSource !== 'text') {
                <label>
                  <span class="field-label">{{ t('HubScreenBinding') }} <hub-help-tooltip [text]="t('HubScreenBindingHelp')"></hub-help-tooltip></span>
                  <input [name]="screenControlName(path, i, 'binding')" [(ngModel)]="element.binding" (ngModelChange)="syncScreenField()" placeholder="variableName">
                </label>
              }
              @if (element.type === 'output') {
                <label>
                  <span class="field-label">{{ t('LabelLabel') }} <hub-help-tooltip [text]="t('HubScreenControlLabelHelp')"></hub-help-tooltip></span>
                  <input [name]="screenControlName(path, i, 'outputLabel')" [(ngModel)]="element.label" (ngModelChange)="syncScreenField()" placeholder="translation_label">
                </label>
              }
              @if (element.type === 'output') {
                <label class="full-width-field">
                  <span class="field-label">{{ t('HubScreenScript') }} <hub-help-tooltip [text]="t('HubScreenScriptHelp')"></hub-help-tooltip></span>
                  <textarea [name]="screenControlName(path, i, 'script')" [(ngModel)]="element.script" (ngModelChange)="syncScreenField()" placeholder="$amount * 2"></textarea>
                </label>
              }
              @if (element.type === 'output') {
                <label class="checkbox-label model-checkbox" [title]="t('HubScreenValueTypeHelp')">
                  <input [name]="screenControlName(path, i, 'valueType')" type="checkbox" [(ngModel)]="element.valueTypeEnabled" (ngModelChange)="syncScreenField()"> {{ t('HubScreenValueType') }} <hub-help-tooltip [text]="t('HubScreenValueTypeHelp')"></hub-help-tooltip>
                </label>
              }
              @if (element.type === 'output' && element.valueTypeEnabled) {
                <label>
                  {{ t('dataType') }}
                  <input [name]="screenControlName(path, i, 'valueTypeType')" [(ngModel)]="element.valueTypeType" (ngModelChange)="syncScreenField()" placeholder="number">
                </label>
              }
              @if (element.type === 'output' && element.valueTypeEnabled) {
                <label>
                  {{ t('step') }}
                  <input [name]="screenControlName(path, i, 'valueTypeStep')" type="number" step="any" [(ngModel)]="element.valueTypeStep" (ngModelChange)="syncScreenField()">
                </label>
              }
              @if (element.type === 'twocolumns') {
                <p class="empty-state">{{ t('HubScreenTwoColumnsHelp') }}</p>
              }
            </div>
          }
          @if (hasScreenChildren(element) && element.childrenOpen) {
            <ng-container *ngTemplateOutlet="screenElementsEditor; context: {elements: element.children, depth: depth + 1, path: childPath(path, i)}"></ng-container>
          }
        </article>
      }
      @if (elements.length === 0) {
        <p class="empty-state">{{ t('HubNoScreenElements') }}</p>
      }
      <button class="secondary-button add-screen-element-button" type="button" (click)="addScreenElement(elements)"><wa-icon name="plus-lg"></wa-icon>{{ t('HubAddScreenElement') }}</button>
    </div>
  </ng-template>
  @if (activeTab === 'workflow') {
    <section class="card editor-card">
      <hub-workflow-editor [workflow]="data.workflow" [screens]="data.screens" [canUndo]="canUndoWorkflow()" [canRedo]="canRedoWorkflow()" [autosaveStatus]="saveUndoRedoLabel()" (workflowChange)="markWorkflowDirty($event)" (undoRequested)="undoWorkflow()" (redoRequested)="redoWorkflow()"></hub-workflow-editor>
    </section>
  }
  @if (activeTab === 'translations') {
    <section class="card editor-card">
      <hub-translations [embedded]="true" [embeddedProcessDefinitionId]="processDefinitionId"></hub-translations>
    </section>
  }
}
